import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";
import api from "../utils/api";
import { 
  FileText, 
  Plus, 
  Trash2, 
  ChefHat, 
  Layers, 
  AlertTriangle
} from "lucide-react";

const RecipeManager = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState("");

  // Modal State
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Recipe Builder Form State
  const [builderIngredients, setBuilderIngredients] = useState([
    { key: "init-0", ingredientId: "", quantity: "" }
  ]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.user) {
          setUserId(decoded.user.id);
        }
      } catch (err) {
        console.error("Token decoding failed:", err);
      }
    }
  }, []);

  const loadData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [prodRes, recRes, invRes] = await Promise.all([
        api.get(`get-product-data/${userId}`),
        api.get("recipes"),
        api.get("inventory")
      ]);

      setProducts(prodRes.data || []);
      setRecipes(recRes.data?.data || []);
      setInventory(invRes.data?.data || []);
    } catch (error) {
      console.error("Failed to load recipe manager data:", error);
      toast.error("Failed to load products or recipes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  // Map recipes by productId for fast O(1) matching
  const recipeMap = {};
  recipes.forEach(r => {
    if (r.productId?._id) {
      recipeMap[r.productId._id] = r;
    } else if (r.productId) {
      recipeMap[r.productId] = r;
    }
  });

  const handleOpenBuilder = (product) => {
    console.log("[RecipeManager] Opening builder for product:", product);
    setSelectedProduct(product);
    const existingRecipe = recipeMap[product._id];
    console.log("[RecipeManager] Existing recipe details:", existingRecipe);

    if (existingRecipe) {
      const mapped = existingRecipe.ingredients.map((i, idx) => ({
        key: `existing-${idx}-${Date.now()}-${Math.random()}`,
        ingredientId: i.ingredientId?._id || i.ingredientId,
        quantity: i.quantity
      }));
      console.log("[RecipeManager] Loaded existing ingredients into builder:", mapped);
      setBuilderIngredients(mapped);
    } else {
      console.log("[RecipeManager] Initializing builder with empty single row");
      setBuilderIngredients([{ key: `new-${Date.now()}-${Math.random()}`, ingredientId: "", quantity: "" }]);
    }
    setShowBuilder(true);
  };


  const handleRemoveRow = (index) => {
    console.log("[RecipeManager] Removing row at index:", index);
    setBuilderIngredients(prev => {
      const updated = prev.filter((_, idx) => idx !== index);
      console.log("[RecipeManager] Ingredients after row removal:", updated);
      return updated;
    });
  };

  const handleIngredientChange = (index, field, value) => {
    console.log(`[RecipeManager] Change field [${field}] to value [${value}] at index [${index}]`);
    setBuilderIngredients(prev => {
      const updated = prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row));
      console.log("[RecipeManager] Updated ingredients state:", updated);
      return updated;
    });
  };

  // Calculate live recipe values during composition
  const getIngredientDetails = (id) => {
    return inventory.find(i => i._id === id);
  };

  const calculateLiveTotals = () => {
    let totalCost = 0;
    builderIngredients.forEach(item => {
      const ingredient = getIngredientDetails(item.ingredientId);
      if (ingredient && item.quantity) {
        totalCost += ingredient.costPerUnit * Number(item.quantity);
      }
    });

    const salePrice = selectedProduct?.price || 0;
    const margin = salePrice - totalCost;
    const costPercentage = salePrice > 0 ? (totalCost / salePrice) * 100 : 0;

    return {
      totalCost: Math.round(totalCost * 100) / 100,
      margin: Math.round(margin * 100) / 100,
      costPercentage: Math.round(costPercentage * 10) / 10
    };
  };

  const { totalCost: liveCost, costPercentage: livePct } = calculateLiveTotals();

  const handleSaveRecipe = async (e) => {
    e.preventDefault();

    // Validate inputs
    const filtered = builderIngredients.filter(i => i.ingredientId && i.quantity > 0);
    if (filtered.length === 0) {
      toast.error("Please add at least one valid ingredient with quantity.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("recipes", {
        productId: selectedProduct._id,
        ingredients: filtered
      });

      if (res.data && res.data.success) {
        toast.success("Recipe composition saved successfully!");
        loadData();
        setShowBuilder(false);
      }
    } catch (error) {
      console.error("Save recipe error:", error);
      toast.error(error.response?.data?.message || "Failed to save recipe");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRecipe = (recipe) => {
    Swal.fire({
      title: "Remove Recipe?",
      text: "This will delete the recipe card composition.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await api.delete(`recipes/${recipe._id}`);
          if (res.data && res.data.success) {
            toast.success("Recipe removed successfully");
            loadData();
            setShowBuilder(false);
          }
        } catch (error) {
          console.error("Delete recipe error:", error);
          toast.error("Failed to delete recipe");
        }
      }
    });
  };

  return (
    <div className="container mx-auto px-4 mt-20 pb-10">
      <ToastContainer />

      {/* Header Panel */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 rounded-3xl p-6 md:p-8 text-white shadow-xl mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <ChefHat className="w-8 h-8 text-emerald-200" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold font-serif">Recipe Manager & Costings</h1>
            <p className="text-emerald-100 text-sm mt-0.5">Map menu products to raw ingredients, analyze serving costs, and optimize profit margins.</p>
          </div>
        </div>
      </div>

      {/* Main Panel */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 font-medium">Loading recipe costing list...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-semibold">No products configured</p>
            <p className="text-sm mt-1">Configure products in menu management first.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Sale Price</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Food Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Margin</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Food Cost %</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Recipe Config</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => {
                  const recipe = recipeMap[product._id];
                  const foodCost = recipe ? recipe.totalCost : null;
                  const margin = foodCost !== null ? product.price - foodCost : null;
                  const costPct = foodCost !== null && product.price > 0 ? (foodCost / product.price) * 100 : null;

                  return (
                    <tr key={product._id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        {product.productName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-semibold">
                        ₹{product.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold">
                        {foodCost !== null ? (
                          <span className="text-gray-700">₹{foodCost.toFixed(2)}</span>
                        ) : (
                          <span className="text-gray-300 italic">No recipe</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold">
                        {margin !== null ? (
                          <span className={margin >= 0 ? "text-green-600" : "text-red-600"}>
                            ₹{margin.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {costPct !== null ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            costPct <= 30 
                              ? "bg-green-100 text-green-800" 
                              : costPct <= 40 
                                ? "bg-amber-100 text-amber-800" 
                                : "bg-red-100 text-red-800"
                          }`}>
                            {costPct.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <button
                          onClick={() => handleOpenBuilder(product)}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition duration-150 ${
                            recipe 
                              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" 
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {recipe ? (
                            <>
                              <Layers className="w-3.5 h-3.5" /> Edit Recipe
                            </>
                          ) : (
                            <>
                              <Plus className="w-3.5 h-3.5" /> Add Recipe
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slide-out/Modal: Recipe Builder */}
      {showBuilder && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm flex items-center justify-end">
          <div className="bg-white h-full max-w-lg w-full shadow-2xl p-6 flex flex-col justify-between border-l border-gray-100 overflow-y-auto">
            
            {/* Modal Header */}
            <div>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-extrabold font-serif text-teal-800">Recipe Composition</h2>
                  <p className="text-sm text-gray-500 font-semibold">{selectedProduct?.productName}</p>
                </div>
                <button
                  onClick={() => setShowBuilder(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Live Costing Analytics Cards */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 text-center">
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Serving Price</span>
                  <span className="text-sm font-bold text-gray-700">₹{selectedProduct?.price.toFixed(2)}</span>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-3 border border-emerald-100 text-center">
                  <span className="block text-[10px] text-emerald-700 font-bold uppercase">Food Cost</span>
                  <span className="text-sm font-bold text-emerald-800">₹{liveCost.toFixed(2)}</span>
                </div>
                <div className={`rounded-2xl p-3 border text-center ${
                  livePct <= 30 
                    ? "bg-green-50 border-green-100 text-green-800" 
                    : livePct <= 40 
                      ? "bg-amber-50 border-amber-100 text-amber-800" 
                      : "bg-red-50 border-red-100 text-red-800"
                }`}>
                  <span className="block text-[10px] font-bold uppercase">Margin %</span>
                  <span className="text-sm font-bold">{livePct > 0 ? (100 - livePct).toFixed(1) : "100"}%</span>
                </div>
              </div>

              {/* Recipe rows form */}
              <form onSubmit={handleSaveRecipe} className="space-y-4">
                <div className="flex items-center justify-between font-bold text-sm text-gray-600 border-b border-gray-100 pb-2 mb-2">
                  <span>Ingredients Composition</span>
                  <span
                    role="button"
                    onClick={(e) => {
                      e.preventDefault();
                      console.log("[RecipeManager] Add Row clicked");
                      setBuilderIngredients(prev => {
                        const updated = [...(prev || []), { key: `new-${Date.now()}-${Math.random()}`, ingredientId: "", quantity: "" }];
                        console.log("[RecipeManager] Ingredients after row added:", updated);
                        return updated;
                      });
                    }}
                    className="text-teal-600 hover:text-teal-700 text-xs flex items-center gap-1 font-bold cursor-pointer select-none"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Row
                  </span>
                </div>

                {inventory.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center my-4 shadow-sm">
                    <AlertTriangle className="w-10 h-10 mx-auto text-amber-600 mb-3 animate-bounce" />
                    <h3 className="text-sm font-bold text-amber-950 mb-1">No Raw Materials Found</h3>
                    <p className="text-xs text-amber-800 font-medium mb-4 leading-relaxed max-w-xs mx-auto">
                      You must configure raw ingredients in your inventory before composing recipes.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBuilder(false);
                        navigate("/inventory");
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow transition duration-150"
                    >
                      Go to Inventory Setup
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                    {console.log("[RecipeManager Render] rendering ingredients. Inventory list:", inventory, "builderIngredients:", builderIngredients)}
                    {(builderIngredients || []).map((row, idx) => {
                      const ingDetail = getIngredientDetails(row.ingredientId);
                      
                      return (
                        <div key={row.key || idx} className="flex gap-2 items-center bg-gray-50/50 p-2.5 rounded-2xl border border-gray-100">
                          {/* Ingredient Select */}
                          <div className="flex-1 min-w-0">
                            <select
                              value={row.ingredientId}
                              onChange={(e) => handleIngredientChange(idx, "ingredientId", e.target.value)}
                              className="w-full text-sm bg-white border border-gray-200 px-3 py-2 rounded-xl focus:ring-1 focus:ring-teal-500 focus:outline-none"
                              required
                            >
                              <option value="">Select ingredient...</option>
                              {inventory.map(i => (
                                <option key={i._id} value={i._id}>{i.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Quantity Input */}
                          <div className="w-24 shrink-0 flex items-center bg-white border border-gray-200 rounded-xl px-2.5 py-1">
                            <input
                              type="number"
                              step="any"
                              value={row.quantity}
                              onChange={(e) => handleIngredientChange(idx, "quantity", e.target.value)}
                              className="w-full text-sm font-semibold focus:outline-none text-right"
                              placeholder="Qty"
                              required
                            />
                            {ingDetail?.unit && (
                              <span className="text-xs text-gray-400 ml-1 font-medium select-none">{ingDetail.unit}</span>
                            )}
                          </div>

                          {/* Row details / Cost */}
                          <div className="w-16 shrink-0 text-right text-xs font-bold text-gray-600">
                            {ingDetail && row.quantity ? `₹${(ingDetail.costPerUnit * Number(row.quantity)).toFixed(1)}` : "₹0.0"}
                          </div>

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(idx)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </form>
            </div>

            {/* Bottom Actions */}
            <div className="border-t border-gray-100 pt-4 mt-6">
              {liveCost > selectedProduct?.price && (
                <div className="flex items-center gap-2 bg-red-100 text-red-800 p-3 rounded-2xl text-xs font-semibold mb-4 border border-red-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>Warning: Food Cost exceeds serving price! Product operates at a loss.</span>
                </div>
              )}

              <div className="flex gap-2">
                {recipeMap[selectedProduct?._id] && (
                  <button
                    type="button"
                    onClick={() => handleDeleteRecipe(recipeMap[selectedProduct._id])}
                    className="p-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-2xl shadow-sm transition duration-200"
                    title="Delete recipe composition"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => setShowBuilder(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition duration-200 text-sm"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveRecipe}
                  disabled={submitting || inventory.length === 0}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-2xl shadow transition duration-200 text-sm disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Save Recipe"}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeManager;
